/**
 * @file src/offline/offlineCoverSource.js
 * @description Turning a stored cover into something an `<img>` can actually show.
 *
 * The bytes are in Cache Storage, under a logical key, behind a same-origin request URL that no
 * server answers — it is a NAME, not an address. Putting that name in a `src` fetches nothing. So
 * the entry is read out of the bucket and wrapped in an object URL, which is a real address the
 * browser will draw.
 *
 * The remote URL stays as the fallback, deliberately: with a connection it works and costs nothing
 * extra, and the alternative — no picture — is worse than a request. What must never happen is the
 * reverse, a page that only ever knows the remote URL and therefore shows a broken image on the one
 * occasion it is opened with no network.
 *
 * ## Object URLs are revoked
 *
 * Each one pins its blob in memory until released. A library of ten covers is not a leak worth
 * worrying about once; a page that re-reads on every library change, which this one does, would
 * mint a new set each time and never let the old ones go.
 */
import { toCacheRequestUrl } from './offlineKeys.js';
import { AUDIO_CACHE_NAME } from '../audio/playback/audioCacheStore.js';

/**
 * A drawable address for one stored cover.
 *
 * @param {string|null} identityKey the cover's logical key, from the manifest
 * @param {string|null} fallbackUrl the remote URL, used when the bytes are not here
 * @returns {Promise<{ src: string|null, fromCache: boolean }>}
 */
export async function resolveCoverSrc(identityKey, fallbackUrl = null) {
  const requestUrl = toCacheRequestUrl(identityKey);
  if (requestUrl && typeof caches !== 'undefined') {
    try {
      const bucket = await caches.open(AUDIO_CACHE_NAME);
      const hit = await bucket.match(requestUrl);
      if (hit) {
        return { src: URL.createObjectURL(await hit.blob()), fromCache: true };
      }
    } catch {
      // A bucket that will not open is the fallback's problem, not an error worth surfacing on a
      // page whose job is to still work when things are unavailable.
    }
  }
  return { src: fallbackUrl || null, fromCache: false };
}

/** Release an address minted above. Safe to call with anything, including a remote URL. */
export function releaseCoverSrc(src) {
  if (typeof src === 'string' && src.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(src);
    } catch {
      // Already gone.
    }
  }
}
